/**
 * Command to link two disconnected workflow branches.
 *
 * Validates:
 * - Both materials are endpoints (roots or leaves)
 * - Materials are in different connected components
 * - No cycle would be created
 * - Not a duplicate link
 *
 * Performs:
 * - Creates a dashed link edge (edgeKind: 'graphsLink')
 * - Renumbers creationOrder in the target branch
 */

import { generateId } from '@/graph/utils/id';
import {
  getConnectedComponents,
  isRootMaterial,
  isLeafMaterial,
  wouldCreateCycle,
  getLinkCandidates,
} from '@/graph/domain/topology';
import { shiftCreationOrdersInComponent, normalizeCreationOrders } from './helpers/renumberCreationOrders';
import type { GraphRecords } from '@/graph/domain/types';

// ─────────────────────────────────────────────────────────────────────────────

export interface LinkBranchesPayload {
  /** Material ID in the source branch (must be root or leaf) */
  sourceMaterialId: string;
  /** Material ID in the target branch (must be opposite endpoint type) */
  targetMaterialId: string;
}

export interface LinkBranchesResult {
  /** ID of the newly created link edge */
  linkEdgeId: string;
  /** IDs of operations that had their creationOrder shifted */
  shiftedOperationIds: string[];
  /** Actual source material of the edge (leaf material) */
  edgeSourceMaterialId: string;
  /** Actual target material of the edge (root material) */
  edgeTargetMaterialId: string;
}

// ─────────────────────────────────────────────────────────────────────────────

export class LinkBranchesError extends Error {
  constructor(
    public code:
      | 'SOURCE_NOT_FOUND'
      | 'TARGET_NOT_FOUND'
      | 'SAME_COMPONENT'
      | 'INVALID_ENDPOINT_PAIR'
      | 'WOULD_CREATE_CYCLE'
      | 'ALREADY_LINKED',
    message: string,
  ) {
    super(message);
    this.name = 'LinkBranchesError';
  }
}

// ─────────────────────────────────────────────────────────────────────────────

export function linkBranches(
  graphData: Pick<GraphRecords, 'materialNodes' | 'operationNodes' | 'flowEdges'>,
  payload: LinkBranchesPayload,
): LinkBranchesResult {
  const { sourceMaterialId, targetMaterialId } = payload;

  // Validate materials exist
  if (!graphData.materialNodes[sourceMaterialId]) {
    throw new LinkBranchesError(
      'SOURCE_NOT_FOUND',
      `Source material ${sourceMaterialId} not found`,
    );
  }
  if (!graphData.materialNodes[targetMaterialId]) {
    throw new LinkBranchesError(
      'TARGET_NOT_FOUND',
      `Target material ${targetMaterialId} not found`,
    );
  }

  // Cannot link a material to itself
  if (sourceMaterialId === targetMaterialId) {
    throw new LinkBranchesError(
      'SAME_COMPONENT',
      'Cannot link a material to itself',
    );
  }

  // Classify endpoints
  const srcIsRoot = isRootMaterial(sourceMaterialId, graphData.flowEdges);
  const srcIsLeaf = isLeafMaterial(sourceMaterialId, graphData.flowEdges);
  const tgtIsRoot = isRootMaterial(targetMaterialId, graphData.flowEdges);
  const tgtIsLeaf = isLeafMaterial(targetMaterialId, graphData.flowEdges);

  if (!srcIsRoot && !srcIsLeaf) {
    throw new LinkBranchesError(
      'INVALID_ENDPOINT_PAIR',
      `Source material ${sourceMaterialId} is neither root nor leaf`,
    );
  }

  if (!tgtIsRoot && !tgtIsLeaf) {
    throw new LinkBranchesError(
      'INVALID_ENDPOINT_PAIR',
      `Target material ${targetMaterialId} is neither root nor leaf`,
    );
  }

  // Validate opposite endpoint types
  const validPair =
    (srcIsRoot && tgtIsLeaf) || (srcIsLeaf && tgtIsRoot);
  if (!validPair) {
    throw new LinkBranchesError(
      'INVALID_ENDPOINT_PAIR',
      `Invalid pair: source is ${srcIsRoot ? 'root' : 'leaf'}, target is ${
        tgtIsRoot ? 'root' : 'leaf'
      }. Must be opposite endpoints.`,
    );
  }

  // Find connected components
  const components = getConnectedComponents({
    materialNodes: graphData.materialNodes,
    operationNodes: graphData.operationNodes,
    flowEdges: graphData.flowEdges,
  });

  const srcComponent = components.find((c) =>
    c.materialIds.has(sourceMaterialId),
  );
  const tgtComponent = components.find((c) =>
    c.materialIds.has(targetMaterialId),
  );

  if (!srcComponent || !tgtComponent) {
    throw new LinkBranchesError(
      'SAME_COMPONENT',
      'Could not determine component membership',
    );
  }

  if (srcComponent.componentId === tgtComponent.componentId) {
    throw new LinkBranchesError(
      'SAME_COMPONENT',
      'Cannot link materials in the same connected component',
    );
  }

  // Check for existing link between these two materials
  const existingLink = Object.values(graphData.flowEdges).find(
    (e) =>
      e.edgeKind === 'graphsLink' &&
      ((e.sourceId === sourceMaterialId && e.targetId === targetMaterialId) ||
        (e.sourceId === targetMaterialId && e.targetId === sourceMaterialId)),
  );

  if (existingLink) {
    throw new LinkBranchesError(
      'ALREADY_LINKED',
      'These materials are already linked',
    );
  }

  // Check for cycles
  if (wouldCreateCycle(graphData, sourceMaterialId, targetMaterialId)) {
    throw new LinkBranchesError(
      'WOULD_CREATE_CYCLE',
      'Creating this link would form a cycle',
    );
  }

  // Determine edge direction: ALWAYS leaf → root (independent of click order)
  // Leaf material = no outgoing flow edges (end of chain)
  // Root material = no incoming flow edges (start of chain)
  
  const srcHasOutgoing = Object.values(graphData.flowEdges).some(
    (e) => e.sourceId === sourceMaterialId && e.edgeKind !== 'graphsLink',
  );
  const tgtHasOutgoing = Object.values(graphData.flowEdges).some(
    (e) => e.sourceId === targetMaterialId && e.edgeKind !== 'graphsLink',
  );

  const srcHasIncoming = Object.values(graphData.flowEdges).some(
    (e) => e.targetId === sourceMaterialId && e.edgeKind !== 'graphsLink',
  );
  const tgtHasIncoming = Object.values(graphData.flowEdges).some(
    (e) => e.targetId === targetMaterialId && e.edgeKind !== 'graphsLink',
  );

  // Identify leaf (no outgoing) and root (no incoming)
  const sourceIsLeaf = !srcHasOutgoing;
  const sourceIsRoot = !srcHasIncoming;
  const targetIsLeaf = !tgtHasOutgoing;
  const targetIsRoot = !tgtHasIncoming;

  let leafMaterialId: string;
  let rootMaterialId: string;

  if (sourceIsLeaf && targetIsRoot) {
    leafMaterialId = sourceMaterialId;
    rootMaterialId = targetMaterialId;
  } else if (sourceIsRoot && targetIsLeaf) {
    leafMaterialId = targetMaterialId;
    rootMaterialId = sourceMaterialId;
  } else {
    // Fallback: use original logic (shouldn't happen with valid endpoints)
    leafMaterialId = sourceIsLeaf ? sourceMaterialId : targetMaterialId;
    rootMaterialId = sourceIsRoot ? sourceMaterialId : targetMaterialId;
  }

  // Edge ALWAYS goes: leaf → root
  const edgeSourceId = leafMaterialId;
  const edgeTargetId = rootMaterialId;

  // ─────────────────────────────────────────────────────────────────────────

  // Create link edge
  const linkEdgeId = generateId('edge');
  graphData.flowEdges[linkEdgeId] = {
    id: linkEdgeId,
    sourceId: edgeSourceId,
    targetId: edgeTargetId,
    edgeKind: 'graphsLink',
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Renormalize creationOrder for ALL chains to ensure proper sequencing
  // ─────────────────────────────────────────────────────────────────────────
  // After creating the link edge, renormalize the entire graph.
  // This ensures: LEAF chain (0,1,...), ROOT chain (n,n+1,...), any other chains after.
  // The normalizeCreationOrders function will handle multiple chains correctly.
  
  const allOperationIds = Object.keys(graphData.operationNodes);
  const shiftedOperationIds = allOperationIds; // All operations may be reordered
  normalizeCreationOrders(graphData);

  return { 
    linkEdgeId, 
    shiftedOperationIds,
    edgeSourceMaterialId: edgeSourceId,  // Leaf material
    edgeTargetMaterialId: edgeTargetId,  // Root material
  };
}
